import type { AuthTokenResponse } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { formatCredits } from '@/shared/utils/format';

const DEFAULT_ENGINE_URL =
  typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'http://127.0.0.1:8000';

const baseUrl = (() => {
  const raw = import.meta.env.VITE_ENGINE_BASE_URL as string | undefined;
  const normalized = raw?.trim() || DEFAULT_ENGINE_URL;
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
})();

function buildUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/*
 * The token used to go in the query string here, which put an access token into
 * Caddy's access log — and any proxy's, and any CDN's — on every socket, where
 * it outlives the token itself. URLs are the one part of a request that is
 * logged by default everywhere.
 *
 * The browser WebSocket API cannot set request headers, so the subprotocol list
 * is the only other place a token can ride. We offer two: a marker the server
 * recognises, then the token. The server echoes the marker back — it must
 * select one of the offered protocols or the browser fails the connection — and
 * never the token.
 */
export const WS_AUTH_PROTOCOL = 'valoi.auth.v1';

export function buildWsUrl(path: string): string {
  const wsBase = baseUrl.replace(/^http(s?):\/\//i, (_, s: string) => `ws${s}://`);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${wsBase}${normalizedPath}`;
}

/** The `protocols` argument for `new WebSocket(url, protocols)`. */
export function wsAuthProtocols(token: string): string[] {
  return [WS_AUTH_PROTOCOL, token];
}

// --- Access token (in-memory only) -----------------------------------------
// The access token is a short-lived JWT. We deliberately keep it out of
// localStorage so it can't be read by XSS; the refresh token lives in an
// HttpOnly cookie and rehydrates this on boot / on 401.
let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
}

// Called when a refresh attempt fails — UserProvider registers a handler that
// drops the app to the unauthenticated state. Kept as a callback to avoid an
// apiClient -> UserProvider import cycle.
let authFailureHandler: (() => void) | null = null;

export function registerAuthFailureHandler(handler: (() => void) | null) {
  authFailureHandler = handler;
}

const REFRESH_PATH = '/api/v1/auth/refresh';

// --- Single-flight refresh --------------------------------------------------
// Concurrent 401s should trigger exactly one /auth/refresh call; everyone awaits
// the same in-flight promise.
let refreshInFlight: Promise<string | null> | null = null;

/*
 * Resolves null only when the engine *looked at the cookie and said no* — a
 * completed, non-ok response. A fetch that never completes (wifi handoff, the
 * engine restarting behind Caddy) THROWS instead: the HttpOnly refresh cookie
 * may be perfectly valid, and one catch-all `return null` here is how a
 * transient blip mid-session used to be indistinguishable from a revoked
 * session — request() would clear the token and bounce the user to /login with
 * "session expired" over nothing.
 */
async function rawRefresh(): Promise<string | null> {
  let response: Response;
  try {
    response = await fetch(buildUrl(REFRESH_PATH), {
      method: 'POST',
      credentials: 'include', // send the HttpOnly refresh cookie
    });
  } catch {
    throw networkError();
  }
  if (!response.ok) return null;
  try {
    const data = (await response.json()) as AuthTokenResponse;
    setAccessToken(data.access_token);
    return data.access_token;
  } catch {
    // A 200 whose body would not read — transient, not a verdict on the session.
    throw networkError();
  }
}

export function performTokenRefresh(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = rawRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Shown whenever a response carries nothing we can honestly put in front of a
// user. The raw body still reaches the console (redacted + truncated) below.
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Error thrown by every request helper on a non-2xx response. Extends Error (so
// existing `catch (e) { e instanceof Error ? e.message : ... }` sites keep working
// with a humanized `.message`) while additionally carrying the HTTP `status` and
// the parsed FastAPI `detail` (string | object | array) so newer call sites can
// branch precisely on billing error kinds — see jobErrors.ts.
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  /** Whole seconds from a `Retry-After` header (429/503), when the server sent one. */
  readonly retryAfterSeconds?: number;

  constructor(status: number, detail: unknown, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

// Parse a FastAPI error body down to its `detail` value. Returns undefined for
// non-JSON bodies (HTML error pages) and JSON without a `detail` key — returning
// the raw text here would let a whole HTML document masquerade as a server-
// authored "human string detail" and get rendered in a banner.
function parseErrorDetail(text: string): unknown {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
      return (parsed as { detail?: unknown }).detail;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// Friendly copy for the known engine/billing error codes (string details and the
// `error` field of object details alike). A string detail not listed here passes
// through as-is (server-authored human copy like the upload-limit message); an
// unrecognized object/array detail gets GENERIC_ERROR_MESSAGE.
//
// A Map, not an object literal, for the reason dictionaryService documents on
// its own lookup table: `code in obj` walks the prototype chain, so a server
// detail of "constructor", "toString" or "valueOf" resolved to a function that
// the Record<string, string> type says cannot be there. The banner then rendered
// "function Object() { [native code] }" at the user. A Map has no prototype to
// walk into, and .get() returns undefined for anything not put in it.
const KNOWN_ERROR_MESSAGES = new Map<string, string>(Object.entries({
  payment_method_required: 'Add a payment method to continue.',
  hold_above_maximum: "This request can't be processed. Try a shorter submission.",
  payment_declined: 'Your card was declined. Try a different card.',
  payment_authentication_required: 'This payment needs additional authentication to complete.',
  amount_below_minimum: 'That amount is below the $0.50 minimum.',
  amount_above_maximum: 'That amount is above the maximum allowed.',
  billing_not_configured: 'Billing is temporarily unavailable. Please try again later.',
  payment_provider_error: 'The payment service is temporarily unavailable. Please try again in a moment.',
  payment_method_management_not_ready: "Managing saved cards isn't available yet.",
  // The engine now returns every billing error as { error: <code> } rather than
  // sometimes a bare string, so codes that previously fell through and displayed
  // themselves verbatim need real copy here.
  insufficient_credits: 'You need more credits to continue. Add funds to your wallet.',
  invalid_cursor: "Couldn't load more activity. Refresh and try again.",
  hold_not_found: 'Something went wrong finalizing this job. Please contact support.',
  // Tax era: every top-up prices sales tax from the billing address, so billing
  // refuses rather than guess a jurisdiction. WalletPage intercepts the first two
  // before the banner and opens the address form instead — this copy is the
  // fallback for anywhere else that surfaces them.
  billing_address_required: 'Add your billing address to continue.',
  billing_address_invalid: "We couldn't verify that billing address. Please check it and try again.",
  tax_unavailable: 'Tax calculation is temporarily unavailable. Please try again in a moment.',
  idempotency_key_reuse: 'That payment was already submitted. Refresh to see its result.',
  job_still_active: 'This job is still running — resume is only for failed jobs.',
  // Character reference review. The engine's own `message` rides alongside these
  // codes, but only the code is read here, so each needs copy of its own.
  reference_review_unsupported:
    "Character review isn't available for this kind of video. Turn it off and try again.",
  operation_in_progress: 'Another regeneration is still running. Wait for it to finish.',
  regeneration_limit_character: 'This character has used all of its regenerations.',
  regeneration_limit_job: 'This video has used all of its character regenerations.',
  not_in_review: 'This video is no longer waiting for a character review.',
  request_id_reuse: 'That request was already used for something different. Please try again.',
  references_changed: 'The characters changed since you last looked. Review the updated images.',
  incomplete_review: 'Every character needs to be reviewed before you can continue.',
  already_approved: 'These characters were already approved.',
  hold_unavailable: "This video's credit hold is no longer active, so it can't be regenerated.",
}));

// Humanize a parsed FastAPI `detail` into banner-ready copy. Pure (no I/O) so it
// is unit-tested directly. Returns '' when there is nothing renderable, letting
// handleResponse fall back to GENERIC_ERROR_MESSAGE.
export function messageForApiError(detail: unknown): string {
  const code =
    typeof detail === 'string'
      ? detail.trim()
      : detail && typeof detail === 'object' && typeof (detail as { error?: unknown }).error === 'string'
        ? ((detail as { error: string }).error)
        : undefined;

  // `required`/`available` arrive as credits, not dollars (valoi_engine's credits
  // API contract). This read them with the USD formatter, so one engine error had
  // two wordings depending on which surface caught it: the pages that go through
  // `describeJobError` said "1.35 credits", while the modals and notifications
  // that render `ApiError.message` said "$1.35". $1 = 1 credit makes the numbers
  // agree, which is exactly why the mislabel survived — and exactly why it
  // matters, since a user cannot tell a units bug from a rounding one.
  if (code === 'insufficient_credits' && detail && typeof detail === 'object') {
    const d = detail as { required?: number; available?: number };
    if (typeof d.required === 'number' && typeof d.available === 'number') {
      return `You need ${formatCredits(d.required)} credits but only have ${formatCredits(d.available)} available. Add funds to continue.`;
    }
  }
  const known = code ? KNOWN_ERROR_MESSAGES.get(code) : undefined;
  if (known) return known;
  // Unrecognized: a plain human string passes through (e.g. a 409 resume message
  // or the upload-size limit); everything else defers to the generic copy.
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  return '';
}

// Thrown when fetch itself rejects (offline, DNS failure, CORS, engine down) —
// i.e. we never got an HTTP response. Modeled as an ApiError with status 0 so the
// many `catch (e) { e instanceof ApiError ... }` sites can treat it uniformly and
// still show friendly copy instead of a raw "Failed to fetch"/"NetworkError".
const NETWORK_ERROR_MESSAGE = 'Network error — please check your connection and try again.';

function networkError(): ApiError {
  return new ApiError(0, { error: 'network_error' }, NETWORK_ERROR_MESSAGE);
}

// Keys whose values must never reach the console, even in a debug build.
//
// `client_secret` is the concrete one: billing's 402 `payment_authentication_required`
// body carries a Stripe client secret (WalletPage reads `detail.client_secret` off
// exactly this shape to drive 3DS), and that value authorizes completing a payment.
// This function used to log the whole raw body of every non-2xx response, so every
// 3DS-required top-up printed one.
const REDACTED_KEYS = ['client_secret', 'clientSecret', 'token', 'secret', 'password'];

// Operates on the RAW TEXT rather than parsed JSON on purpose: the body is not
// guaranteed to be JSON (a proxy's HTML error page, a truncated response), and the
// redaction has to hold for those too. Matches `"key": "value"` and `"key":"value"`.
function redactSensitive(text: string): string {
  return REDACTED_KEYS.reduce(
    (acc, key) =>
      acc.replace(
        new RegExp(`("${key}"\\s*:\\s*)"[^"]*"`, 'gi'),
        (_match, prefix: string) => `${prefix}"[REDACTED]"`,
      ),
    text,
  );
}

// Bodies are also unbounded — an upstream HTML error page would otherwise dump a full
// document into the console on every failed request.
const MAX_LOGGED_BODY_CHARS = 500;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    const safe = redactSensitive(text).slice(0, MAX_LOGGED_BODY_CHARS);
    logger.error(`API Error [${response.status}]:`, safe);
    const detail = parseErrorDetail(text);
    // Never the raw body: anything messageForApiError can't honestly humanize
    // (validation arrays, unknown codes, HTML pages) gets the generic copy —
    // the body itself is already in the console line above.
    const message = messageForApiError(detail) || GENERIC_ERROR_MESSAGE;
    const retryAfter = Number.parseInt(response.headers?.get?.('Retry-After') ?? '', 10);
    throw new ApiError(
      response.status,
      detail,
      message,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export type RequestOptions = {
  // Forwarded to fetch — set 'include' for cookie-bearing auth endpoints.
  credentials?: RequestCredentials;
  // Skip the 401 -> refresh -> retry dance (used by /auth/refresh and /auth/logout
  // themselves, which must not recurse).
  skipAuthRefresh?: boolean;
  // Extra per-call headers (e.g. X-User-Id for the videos backend). Merged on top
  // of the request's own headers; the Authorization Bearer is still added below.
  headers?: Record<string, string>;
  // Lets the caller abandon a request. There is no client-side timeout anywhere in
  // this file, so without this a slow call runs until the proxy gives up — which is
  // why the research-paper panel could show a Cancel button that only hid itself
  // while the upload it started kept running.
  signal?: AbortSignal;
};

/*
 * An abandoned request is not a failed one.
 *
 * Every caller of `request` treats a throw as something to show the user, and
 * `networkError()` below turns any fetch rejection into "check your connection".
 * An abort rejects the same way, so without this a user who pressed Cancel would be
 * told their network was broken. Callers use this to tell the two apart and stay
 * silent for the one they caused.
 */
export function isAbortError(error: unknown): boolean {
  /* By name, not by `instanceof DOMException`, which is narrower than the condition
     this wants in two ways. It is realm-bound -- a DOMException raised by the fetch
     implementation under jsdom is not the jsdom global's, so the check fails and the
     abort gets relabelled "check your connection" in exactly the environment the tests
     run in. And `controller.abort(reason)` rejects with the REASON, which is usually
     not a DOMException at all. The name is what both forms agree on, and an `ApiError`
     still cannot match it. */
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

async function request<T>(path: string, init: RequestInit, options: RequestOptions = {}): Promise<T> {
  const doFetch = () => {
    const headers = new Headers(init.headers);
    if (options.headers) {
      for (const [key, value] of Object.entries(options.headers)) {
        headers.set(key, value);
      }
    }
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return fetch(buildUrl(path), {
      ...init,
      headers,
      credentials: options.credentials ?? init.credentials,
      signal: options.signal ?? init.signal,
    });
  };

  let response: Response;
  try {
    response = await doFetch();
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw networkError();
  }

  if (response.status === 401 && !options.skipAuthRefresh) {
    // A thrown refresh is a network failure, not a rejected session — let it
    // propagate as the request's own error and leave the tokens alone. Only a
    // completed refresh that still leaves us 401 justifies the sign-out below.
    const refreshed = await performTokenRefresh();
    if (refreshed) {
      try {
        response = await doFetch(); // retry once with the refreshed token
      } catch (error) {
        if (isAbortError(error)) throw error;
        throw networkError();
      }
    }
    if (response.status === 401) {
      clearAccessToken();
      authFailureHandler?.();
    }
  }

  return handleResponse<T>(response);
}

export async function getJson<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>(path, { method: 'GET' }, options);
}

export async function deleteJson<T>(path: string, options?: RequestOptions): Promise<T> {
  // The engine returns 204 for a successful delete; handleResponse maps that to
  // undefined, so callers typically use Promise<void>.
  return request<T>(path, { method: 'DELETE' }, options);
}

export async function postJson<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export async function putJson<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export async function patchJson<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export async function postMultipart<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
  // Routed through request() so the upload carries the Bearer token and survives a
  // 401 -> refresh -> retry, same as our JSON calls. We deliberately do NOT set a
  // Content-Type header: fetch derives the multipart boundary from the FormData body.
  return request<T>(path, { method: 'POST', body: formData }, options);
}

export function resolveApiUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  return buildUrl(path);
}

// Optional CloudFront base for video streaming. Unset today; when backend
// uploads mp4s to S3 behind a distribution, set VITE_CLOUDFRONT_BASE_URL
// and stream URLs will go through CloudFront instead of the engine origin.
const cloudfrontBaseUrl = (() => {
  const raw = import.meta.env.VITE_CLOUDFRONT_BASE_URL as string | undefined;
  const normalized = raw?.trim();
  if (!normalized) return undefined;
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
})();

export function resolveStreamUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  // Absolute URLs (backend already returned a CDN URL) pass through.
  if (/^https?:\/\//i.test(path)) return path;
  if (cloudfrontBaseUrl) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${cloudfrontBaseUrl}${normalizedPath}`;
  }
  return resolveApiUrl(path);
}
